"use client";

import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";

/* Public site navbar — Figma "Project GenRYZ" header, extended with the
   mega-menu pattern from the reference shot (column panel, divider rules,
   arrow affordance per row).

   A link is either a plain route (`to`/`href`) or a dropdown (`menu`):

     { label: "Learning", menu: { groups: [
         { title: "Courses", items: [ { label, to, desc? } ] },
     ] } }

   One group is one panel column, so keep the groups similar in length — that
   even fill is what the layout is tuned for. */

const DEFAULT_LINKS = [
  { label: "Home", to: "/" },
  { label: "Pricing", to: "/package" },
  { label: "Docs", to: "/courses" },
  { label: "Projects", to: "/upcoming" },
];

/* The current page's tab wears a lime pill. Only exact matches count — "/" would
   otherwise light up on every route. */
function useIsActive(to) {
  const { pathname } = useLocation();
  return Boolean(to) && pathname === to;
}

const ACTIVE_PILL = "rounded-full bg-[var(--lime)] px-3.5 py-1";

/* `pill` is off in the mobile sheet, where rows are full-width and a pill would
   stretch across the panel instead of hugging the label. */
function NavLink({ item, onClick, className = "", pill = true }) {
  const active = useIsActive(item.to) && pill;
  const classes = `${className} font-poppins text-[18px] leading-[34px] whitespace-nowrap text-black ${active ? ACTIVE_PILL : ""}`;

  if (item.to) {
    return (
      <Link to={item.to} className={classes} onClick={onClick}>
        {item.label}
      </Link>
    );
  }

  return (
    <a href={item.href || "#"} className={classes} onClick={onClick}>
      {item.label}
    </a>
  );
}

function CtaButton({ label, href, onClick, className = "" }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={`${className} group font-poppins inline-flex items-center justify-center gap-2 rounded-[45px] bg-[var(--lime)] pl-[22px] pr-[11px] py-[10px] text-[20px] leading-[34px] text-black hover:brightness-95 active:scale-95 transition-all`}
    >
      {label}
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-black">
        <ArrowUpRight
          size={18}
          className="text-white transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </span>
    </a>
  );
}

/* One row inside a mega-menu column — a hairline-separated list item, as in the
   reference: label left, arrow pinned to the column's right edge. */
function MenuRow({ item, onClick, divided }) {
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={`group/row flex items-center justify-between gap-6 rounded-[12px] px-4 py-3 transition-colors hover:bg-[var(--fig-surface)] ${
        divided ? "border-t border-black/[0.07]" : ""
      }`}
    >
      <span className="min-w-0">
        <span className="block font-poppins text-[17px] leading-6 text-black">{item.label}</span>
        {item.desc && (
          <span className="mt-1 block font-poppins text-[14px] leading-5 text-[#A7A7A7]">
            {item.desc}
          </span>
        )}
      </span>
      <ArrowUpRight
        size={17}
        className="shrink-0 self-center text-[#B5B5B5] transition-all group-hover/row:text-black group-hover/row:translate-x-0.5 group-hover/row:-translate-y-0.5"
      />
    </Link>
  );
}

function MenuColumn({ group, onNavigate, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      {group.title && (
        <p className="px-4 font-poppins text-[14px] uppercase tracking-[0.16em] text-[#9A9A9A]">
          {group.title}
        </p>
      )}
      <div className={`flex flex-col ${group.title ? "mt-4" : ""}`}>
        {group.items.map((item, i) => (
          <MenuRow key={item.label} item={item} onClick={onNavigate} divided={i > 0} />
        ))}
      </div>
    </div>
  );
}

/* Desktop dropdown trigger. Hover opens it for pointer users; click and
   Enter/Space toggle it, so it is reachable by keyboard and on touch laptops
   alike.

   An item carrying both `to` and `menu` (Learning) is a landing page *and* a
   dropdown: the label becomes a real link and only the chevron toggles, so a
   click on the word navigates instead of being swallowed by the menu. Items
   with `menu` alone keep the whole trigger as the toggle button.

   The trigger draws no panel of its own — every menu is full-bleed, so the
   panel is rendered by the navbar root instead (see MegaPanel). */
function DesktopMenuItem({ item, isOpen, onOpen, onClose, onToggle }) {
  const active = useIsActive(item.to);
  const labelClasses = `font-poppins text-[18px] leading-[34px] whitespace-nowrap text-black ${active ? ACTIVE_PILL : ""}`;
  const chevron = (
    <ChevronDown
      size={16}
      className={`mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
    />
  );

  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onFocus={onOpen}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) onClose();
      }}
    >
      {item.to ? (
        <span className="flex items-center gap-1.5">
          <Link to={item.to} className={labelClasses}>
            {item.label}
          </Link>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-label={`${isOpen ? "Close" : "Open"} ${item.label} menu`}
            className="flex items-center text-black"
          >
            {chevron}
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
          className={`flex items-center gap-1.5 ${labelClasses}`}
        >
          {item.label}
          {chevron}
        </button>
      )}
    </div>
  );
}

/* Mega panel.

   It is a child of the navbar root rather than of the trigger, so it is free of
   the row's flex layout and can be centred on its own. The card takes the same
   `max-w-7xl` as the navbar row above it — the two share one width and one
   centre line — with the hero panel's `rounded-[24px]` and gutter.

   A menu that carries a single group (Portals) would leave most of that width
   empty, so its items become the columns instead. Either way the column count
   comes from the config rather than a fixed grid, which is what keeps the
   columns evenly filled instead of one deep list beside empty space. */
function MegaPanel({ item, onOpen, onClose }) {
  const { groups } = item.menu;
  const single = groups.length === 1;
  const columns = single
    ? groups[0].items.map((entry) => ({ title: null, items: [entry] }))
    : groups;
  const gridCols = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" }[columns.length] || "grid-cols-3";

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2, ease: [0.22, 0.9, 0.3, 1] }}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      className="absolute inset-x-0 top-full z-40 hidden px-3 sm:px-4 lg:block"
    >
      <div className="mx-auto w-full max-w-7xl overflow-hidden rounded-[24px] border border-black/[0.06] bg-white shadow-[0_28px_70px_-24px_rgba(0,0,0,0.28)]">
        <div className="px-2 py-8 xl:py-9">
          {/* The single-group menu keeps one eyebrow above the whole grid rather
              than repeating it over every column. */}
          {single && (
            <p className="px-4 font-poppins text-[14px] uppercase tracking-[0.16em] text-[#9A9A9A]">
              {groups[0].title}
            </p>
          )}
          <div className={`grid gap-x-2 ${gridCols} ${single ? "mt-4" : ""}`}>
            {columns.map((group, i) => (
              <MenuColumn
                key={group.title || group.items[0].label}
                group={group}
                onNavigate={onClose}
                className={i > 0 ? "border-l border-black/[0.07] pl-2" : ""}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* Mobile accordion section for a dropdown link. As on desktop, an item with its
   own `to` splits the row: the label navigates, the chevron expands. */
function MobileMenuItem({ item, isOpen, onToggle, onNavigate }) {
  const chevron = (
    <ChevronDown
      size={18}
      className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
    />
  );

  return (
    <div className="border-b border-black/10 pb-3">
      {item.to ? (
        <div className="flex w-full items-center justify-between py-1">
          <Link
            to={item.to}
            onClick={onNavigate}
            className="font-poppins text-[18px] leading-[34px] text-black"
          >
            {item.label}
          </Link>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Collapse" : "Expand"} ${item.label} menu`}
            className="flex items-center py-1 pl-4 text-black"
          >
            {chevron}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex w-full items-center justify-between py-1 font-poppins text-[18px] leading-[34px] text-black"
        >
          {item.label}
          {chevron}
        </button>
      )}

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 0.9, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-4 pt-3">
              {item.menu.groups.map((group) => (
                <div key={group.title}>
                  <p className="px-1 font-poppins text-[12px] uppercase tracking-[0.14em] text-[#A7A7A7]">
                    {group.title}
                  </p>
                  <div className="mt-1.5 flex flex-col">
                    {group.items.map((sub) => (
                      <Link
                        key={sub.label}
                        to={sub.to}
                        onClick={onNavigate}
                        className="flex items-center justify-between gap-4 px-1 py-2 font-poppins text-[16px] text-black"
                      >
                        {sub.label}
                        <ArrowUpRight size={16} className="shrink-0 text-[#A7A7A7]" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const Navbar1 = ({
  links = DEFAULT_LINKS,
  logo,
  ctaLabel = "Get Started",
  onCtaClick,
  ctaHref = "#",
  secondaryAction,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);        // desktop dropdown label
  const [openSection, setOpenSection] = useState(null);  // mobile accordion label
  const { pathname, hash } = useLocation();
  const closeTimer = useRef(null);

  const closeMenu = () => {
    setIsOpen(false);
    setOpenSection(null);
  };
  const toggleMenu = () => setIsOpen((open) => !open);

  // A short close delay keeps the menu alive while the pointer crosses between
  // two adjacent triggers, which otherwise reads as a flicker.
  const openDropdown = (label) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const closeDropdown = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Same-route hash links ("/#skill-paths") do not unmount anything, so both
  // panels are closed off the location rather than off the click.
  useEffect(() => {
    setOpenMenu(null);
    closeMenu();
  }, [pathname, hash]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setOpenMenu(null);
      closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The mobile overlay is fixed and full-height — let the page behind it stay put.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleCtaClick = (event) => {
    if (onCtaClick) {
      event.preventDefault();
      onCtaClick();
    }
    closeMenu();
  };

  const activeMenu = links.find((l) => l.label === openMenu && l.menu);

  return (
    <div className="relative flex justify-center w-full py-4 px-4">
      <div className="flex items-center justify-between w-full max-w-7xl min-h-[56px] relative z-50">
        {logo && (
          <div className="flex items-center min-w-0">
            <motion.div
              className="flex items-center"
              initial={{ scale: 0.96 }}
              animate={{ scale: 1 }}
              whileHover={{ rotate: 1, scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              {logo}
            </motion.div>
          </div>
        )}

        {/* Five items plus the CTA is tight at exactly 1024px — the gap only
            opens up to the Figma spacing once there is room for it. */}
        <nav className="hidden lg:flex flex-1 items-center justify-center gap-6 xl:gap-10">
          {links.map((item) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              /* The dropdown triggers skip the hover scale — a moving trigger
                 under an open panel reads as a wobble. */
              whileHover={item.menu ? undefined : { scale: 1.04 }}
              className="relative"
            >
              {item.menu ? (
                <DesktopMenuItem
                  item={item}
                  isOpen={openMenu === item.label}
                  onOpen={() => openDropdown(item.label)}
                  onClose={closeDropdown}
                  onToggle={() =>
                    openMenu === item.label ? setOpenMenu(null) : openDropdown(item.label)
                  }
                />
              ) : (
                <NavLink item={item} />
              )}
            </motion.div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {secondaryAction}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            whileHover={{ scale: 1.04 }}
          >
            <CtaButton label={ctaLabel} href={ctaHref} onClick={handleCtaClick} />
          </motion.div>
        </div>

        <motion.button
          className="lg:hidden flex items-center p-1 ml-auto"
          onClick={toggleMenu}
          whileTap={{ scale: 0.9 }}
          aria-label="Open navigation menu"
        >
          <Menu className="h-6 w-6 text-black" />
        </motion.button>
      </div>

      {/* Sits outside the max-w-7xl row so it can span the viewport. The root's
          pb-4 is the gap the pointer crosses between trigger and panel — the
          close delay plus the panel's own onMouseEnter keep it open across it. */}
      <AnimatePresence>
        {activeMenu && (
          <MegaPanel
            key={activeMenu.label}
            item={activeMenu}
            onOpen={() => openDropdown(activeMenu.label)}
            onClose={closeDropdown}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[80] overflow-y-auto bg-white/95 px-6 pb-12 pt-24 backdrop-blur-2xl lg:hidden"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <motion.button
              className="absolute top-6 right-6 p-2"
              onClick={toggleMenu}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              aria-label="Close navigation menu"
            >
              <X className="h-6 w-6 text-black" />
            </motion.button>

            <div className="flex flex-col space-y-4">
              {links.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 + 0.1 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  {item.menu ? (
                    <MobileMenuItem
                      item={item}
                      isOpen={openSection === item.label}
                      onToggle={() =>
                        setOpenSection((s) => (s === item.label ? null : item.label))
                      }
                      onNavigate={closeMenu}
                    />
                  ) : (
                    <div className="border-b border-black/10 pb-3">
                      <NavLink item={item} onClick={closeMenu} className="block w-full py-1" pill={false} />
                    </div>
                  )}
                </motion.div>
              ))}

              {secondaryAction && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  exit={{ opacity: 0, y: 16 }}
                  className="pt-2"
                >
                  {secondaryAction}
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                exit={{ opacity: 0, y: 20 }}
                className="pt-4"
              >
                <CtaButton label={ctaLabel} href={ctaHref} onClick={handleCtaClick} className="w-full" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { Navbar1 };
